// Matching Score Algorithm
// Calculates a weighted score between a candidate application and a job position
// Returns per-criterion raw (0..1), weighted (0..100) and total (0..100) scores
//
// Each LLM-scored criterion (education, tech skills, experience) has its own focused
// prompt; the deterministic criteria (languages, soft skills, position) compare exact
// enum values and need no LLM. All partial scores are combined into the total at the end.

import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { AdornisLLMRouter } from '@adornis/llm/server/llm-router.js';
import sanitizeHtml from 'sanitize-html';
import * as m from '../../../../localization/out/messages.js';
import { setLocale } from '../../../../localization/out/runtime.js';
import { azureFoundryLLM } from '../../../api/llm.js';
import type { JobPosition, SpeechLevel } from '../../../db/job-position.js';
import type { MWUserData } from '../../../db/user.js';
import { parseSkillList } from '../shared/parse-skill-list.js';

/*
  Weights (sum = 1)
  - skills: 0.35 (education 0.25 + tech skills 0.10, scored by separate prompts)
  - languages: 0.25
  - softSkills: 0.10
  - experience: 0.15
  - position: 0.15
*/

export type MatchingScoreResult = {
  total: number; // 0..100
  weighted: {
    skills: number;
    languages: number;
    softSkills: number;
    experience: number;
    position: number;
  };
  raw: {
    skills: number; // 0..1
    languages: number; // 0..1
    softSkills: number; // 0..1
    experience: number; // 0..1
    position: number; // 0..1
  };
  notes: string[]; // diagnostics (e.g., unmet requirements)
};

export const MATCH_WEIGHTS = {
  skills: 0.35,
  languages: 0.25,
  softSkills: 0.1,
  experience: 0.15,
  position: 0.15,
} as const;

// The published `skills` criterion is composed of two separately prompted scores.
const EDUCATION_WEIGHT = 0.25;
const TECH_WEIGHT = 0.1;

// ------------------ Helpers ------------------

const cefrToLevel: Record<SpeechLevel, number> = {
  A1: 1,
  A2: 2,
  B1: 3,
  B2: 4,
  C1: 5,
  C2: 6,
  NONE: 0,
  UNKNOWN: 0,
};

const AcademicTitleToLevel: Record<string, number> = {
  other: 0,
  bachelor: 1,
  bsc: 1,
  ba: 1,
  master: 2,
  msc: 2,
  ma: 2,
  doctoral: 3,
  phd: 3,
  doktor: 3,
  staatsexam: 2, // treat Staatsexamen as comparable to a Master's level
};

function academicTitleLevel(raw?: string): number {
  const s = slugify(raw || '') || '';
  if (!s) return 0;
  return AcademicTitleToLevel[s] ?? 0;
}

function slugify(s?: string): string | undefined {
  if (!s) return undefined;
  return s
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{Diacritic}+/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function coverage(candidate: string[], required: string[]): number {
  if (required.length === 0) return 1; // nothing required -> full marks
  if (candidate.length === 0) return 0;
  const cset = new Set(candidate);
  let hit = 0;
  for (const r of required) if (cset.has(r)) hit++;
  return hit / required.length;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function plainText(items: Maybe<Array<{ langKey?: Maybe<string>; text?: Maybe<string> }>>): string {
  if (!items) return '';
  const item = items.find(i => i.langKey === 'en') ?? items[0];
  if (!item?.text) return '';
  return sanitizeHtml(item.text, { allowedTags: [] });
}

// ------------------ LLM prompt runner ------------------

type CriterionScore = { score: number; notes: string[] };

const respondInLanguage = (candidate: MWUserData) =>
  `Make sure to respond in "${candidate.chosenLanguage}". This is important, otherwise the output will not be usable.`;

// Runs a single focused scoring prompt and parses the strict-JSON response.
// Throws when no valid score comes back so callers can fall back to their heuristic.
async function runScoringPrompt(system: string, user: string): Promise<CriterionScore> {
  const resp = await new AdornisLLMRouter(azureFoundryLLM('gpt-5.4-mini')).chat.completions.create({
    messages: [
      { role: 'system', content: system },
      { role: 'user', content: user },
    ],
    // Prefer JSON when supported; router will adapt per provider
    response_format: { type: 'json_object' },
    stream: false,
  });

  const content = resp?.choices?.[0]?.message?.content ?? (resp as any)?.content ?? '';

  let parsed: any = null;
  if (typeof content === 'string') {
    // Extract JSON if surrounding text slipped in
    const match = content.match(/\{[\s\S]*\}/);
    parsed = JSON.parse(match ? match[0] : content);
  } else if (Array.isArray(content)) {
    const text = content.map((c: any) => (typeof c === 'string' ? c : (c?.text ?? ''))).join('');
    const match = text.match(/\{[\s\S]*\}/);
    parsed = JSON.parse(match ? match[0] : text);
  } else if (content && typeof content === 'object') {
    parsed = content;
  }

  const score = Number(parsed?.score);
  if (!Number.isFinite(score)) throw new Error('Invalid LLM score');
  const notes = Array.isArray(parsed?.notes) ? parsed.notes.map((n: any) => String(n)) : [];
  return { score: clamp01(score), notes };
}

// ------------------ Criterion Scorers ------------------

// Deterministic education fallback based on enum comparisons only.
function deterministicEducation(candidate: MWUserData, job: JobPosition): CriterionScore {
  const notes: string[] = [];
  const parts: number[] = [];

  // Expected academic title: a candidate at or above any expected level counts as a fit.
  if (job.expectedAcademicTitle && Array.isArray(job.expectedAcademicTitle) && job.expectedAcademicTitle.length > 0) {
    const candLevel = academicTitleLevel(candidate.academicTitle || '');
    const ok = job.expectedAcademicTitle.some(title => {
      const reqLevel = academicTitleLevel(String(title));
      return candLevel >= reqLevel;
    });
    parts.push(ok ? 1 : 0);
    if (!ok) notes.push(m.matching_score_algorithm_academic_title_mismatch());
  }

  // Preferred study fields (enum[] on job) vs candidate department (enum)
  if (job.preferredStudyField && job.preferredStudyField.length) {
    const ok = candidate.department ? job.preferredStudyField.includes(candidate.department) : false;
    parts.push(ok ? 1 : 0);
    if (!ok) notes.push(m.matching_score_algorithm_study_field_not_in_list());
  }

  if (parts.length === 0) return { score: 1, notes };
  const score = parts.reduce((a, b) => a + b, 0) / parts.length;
  return { score: clamp01(score), notes };
}

// Education match based on expected academic title and preferred study fields.
// Uses its own focused prompt so related study fields get partial credit instead
// of the binary enum comparison; falls back to the deterministic check on error.
async function scoreEducation(candidate: MWUserData, job: JobPosition): Promise<CriterionScore> {
  const expectsTitle = !!job.expectedAcademicTitle?.length;
  const expectsField = !!job.preferredStudyField?.length;
  if (!expectsTitle && !expectsField) return { score: 1, notes: [] }; // nothing required -> full marks

  const system = [
    'You are a recruiter assistant that evaluates the educational fit between a candidate and a job.',
    'Return a JSON object with keys: score (0..1 number) and notes (array of short strings).',
    'When both an academic title and a study field are expected, they contribute equally to the score.',
    'Academic title: a candidate whose title is at or above any of the expected titles fully satisfies the requirement (e.g. a Master satisfies an expected Bachelor).',
    'Study field: give full credit for an exact or equivalent field, partial credit for closely related fields, and no credit for unrelated fields.',
    'Notes should briefly state unmet or only partially met expectations.',
  ].join(' ');

  const user = [
    'Evaluate educational fit.',
    `Candidate academic title: ${candidate.academicTitle ?? ''}`,
    `Candidate study department: ${candidate.department ?? ''}`,
    `Candidate study field (free text): ${candidate.studyField ?? ''}`,
    `Expected academic titles (any of): ${JSON.stringify(job.expectedAcademicTitle ?? [])}`,
    `Preferred study fields (any of): ${JSON.stringify(job.preferredStudyField ?? [])}`,
    'Respond ONLY with JSON like {"score": 0.0, "notes": ["..."]}.',
    respondInLanguage(candidate),
  ].join('\n');

  try {
    return await runScoringPrompt(system, user);
  } catch (e) {
    logger.warn({ error: e }, 'Education scoring fell back to rules');
    return deterministicEducation(candidate, job);
  }
}

// Technical skills from comma-/semicolon-separated lists
async function scoreTech(candidate: MWUserData, job: JobPosition): Promise<CriterionScore> {
  const notes: string[] = [];

  const cand = parseSkillList(candidate.techSkills);
  const required = parseSkillList(job.mandatoryTechSkills);
  const desired = parseSkillList(job.desiredTechSkills);
  const optional = parseSkillList(job.optionalTechSkills);

  if (required.length === 0 && desired.length === 0 && optional.length === 0) return { score: 1, notes };

  const system = [
    'You are a recruiter assistant that evaluates the technical fit between a candidate and a job.',
    'STEP 1 — Classify every listed skill (required, desired, optional) into exactly one match level: "full" (1.0): the candidate has the exact skill, a widely recognized alias, or a direct synonym (e.g. "React.js" = "React", "GCP" = "Google Cloud Platform"); "partial" (0.5): a closely related or transferable skill/technology in the same domain, but not the same tool (e.g. Vue.js when React is required, SQL Server when PostgreSQL is required); "none" (0.0): no evidence of the skill or a related skill; If uncertain between two levels, always choose the lower one.',
    `STEP 2 — Compute the score using this exact formula (do not estimate)
required_ratio = sum(match values of required skills) / count(required skills)
desired_ratio  = sum(match values of desired skills)  / count(desired skills)
optional_ratio = sum(match values of optional skills)  / count(optional skills)
score = 0.6 * required_ratio + 0.3 * desired_ratio + 0.1 * optional_ratio
If a skill group is empty, drop that group without computing its ratio and renormalise the remaining weights so they sum to 1.`,
    'Desired skills account for 30% of the score, optional skills only for 10% of the score.',
    'Treat synonyms, abbreviations and close variants as matches (e.g. "React.js" matches "React").',
    'Notes should briefly name the missing skills, starting with the required ones.',
  ].join(' ');

  const user = [
    'Evaluate technical fit.',
    `Candidate skills (raw): ${candidate.techSkills ?? ''}`,
    `Candidate skills (normalized): ${JSON.stringify(cand)}`,
    `Required skills: ${JSON.stringify(required)}`,
    `Desired skills: ${JSON.stringify(desired)}`,
    `Optional skills: ${JSON.stringify(optional)}`,
    'Respond ONLY with JSON like {"score": 0.0, "notes": ["..."]}.',
    respondInLanguage(candidate),
  ].join('\n');

  try {
    return await runScoringPrompt(system, user);
  } catch (e) {
    logger.warn({ error: e }, 'Tech skills scoring fell back to rules');
    // Fallback to deterministic heuristic
    const covReq = coverage(cand, required);
    const covDes = coverage(cand, desired);
    const covOpt = coverage(cand, optional);
    const score = clamp01(covReq + 0.5 * covDes + 0.25 * covOpt);

    if (required.length > 0) {
      const needed = new Set(required);
      for (const s of cand) if (needed.has(s)) needed.delete(s);
      if (needed.size > 0)
        notes.push(m.matching_score_algorithm_missing_required_tech_skill({ '+': Array.from(needed).join(', ') }));
    }
    return { score, notes };
  }
}

// Education and tech skills are scored by separate focused prompts and recombined
// into the published `skills` criterion (education 25%, tech 10%, normalized to 35%).
async function scoreSkills(candidate: MWUserData, job: JobPosition): Promise<CriterionScore> {
  const [ed, tech] = await Promise.all([scoreEducation(candidate, job), scoreTech(candidate, job)]);

  const score = (EDUCATION_WEIGHT * ed.score + TECH_WEIGHT * tech.score) / (EDUCATION_WEIGHT + TECH_WEIGHT);

  return { score: clamp01(score), notes: [...ed.notes, ...tech.notes] };
}

function scoreLanguages(candidate: MWUserData, job: JobPosition): CriterionScore {
  const reqs = [
    job.languageRequirementsEN ? { language: 'en', level: job.languageRequirementsEN } : null,
    job.languageRequirementsDE ? { language: 'de', level: job.languageRequirementsDE } : null,
  ].filter(Boolean) as Array<{ language: 'en' | 'de'; level: SpeechLevel }>;

  const candidateLangs = [
    candidate.languageSkillsEN ? { language: 'en', level: candidate.languageSkillsEN } : null,
    candidate.languageSkillsDE ? { language: 'de', level: candidate.languageSkillsDE } : null,
  ].filter(Boolean) as Array<{ language: 'en' | 'de'; level: SpeechLevel }>;

  const notes: string[] = [];
  if (reqs.length === 0) return { score: 1, notes };

  const candMap = new Map<string, number>();
  for (const l of candidateLangs) {
    const lvl = cefrToLevel[l.level];
    if (lvl) candMap.set(l.language, Math.max(candMap.get(l.language) || 0, lvl));
  }

  const per: number[] = [];
  let hardFail = false;

  // If candidate does not meet all requirements, fail hard.
  // Hardfail means 0 points

  // removed hardfail for now. As hardfailing someone is does not grant good results. As that means, they either hard fail or fulfills all requirements.
  // This makes this score a binary 0% or 100%
  for (const r of reqs) {
    const need = cefrToLevel[r.level];
    const have = candMap.get(r.language) || 0;
    const ok = have >= need ? 1 : 0;
    per.push(ok);
    if (!ok) {
      notes.push(
        m.matching_score_algorithm_language_requirement_not_met({ language: r.language.toUpperCase(), level: r.level }),
      );
      // hardFail = true;
    }
  }

  const avg = per.length ? per.reduce((a, b) => a + b, 0) / per.length : 1;
  return { score: hardFail ? 0 : avg, notes };
}

function scoreSoftSkills(
  candidateSoft: Maybe<string[]> | undefined,
  jobSoft: Maybe<string[]> | undefined,
): CriterionScore {
  const cand = Array.isArray(candidateSoft) ? candidateSoft : [];
  const want = Array.isArray(jobSoft) ? jobSoft : [];
  if (want.length === 0) return { score: 1, notes: [] };
  const cset = new Set(cand);
  let hit = 0;
  for (const s of want) if (cset.has(s)) hit++;
  return { score: clamp01(hit / want.length), notes: [] };
}

// Experience based on user's declared practical experience level and descriptions.
// Two focused prompts: one against the job description, one against the job's
// practical-experience requirements; the code weights them 1:2.
async function scoreExperience(candidate: MWUserData, job: JobPosition): Promise<CriterionScore> {
  const levelScoreMap: Record<string, number> = { NONE: 0, FIRST: 0.5, EXTENSIVE: 1 };
  const fallbackLevels = candidate.practicalExperiences?.map(pe =>
    pe.level ? (levelScoreMap[String(pe.level)] ?? 0) : 0,
  ) || [0];
  const fallbackBest = Math.max(...fallbackLevels);
  const fallbackNotes: string[] = fallbackBest === 0 ? [m.matching_score_algorithm_no_practical_experience()] : [];

  const experiences = (candidate.practicalExperiences || []).map(pe => ({
    level: pe.level ?? null,
    description: pe.description ?? '',
  }));

  // If no job description is provided, return fallback score as the LLM would try and match with nothing.
  // But some experience is always better than none.
  if (!job.description && !job.practicalExperience) return { score: clamp01(fallbackBest), notes: fallbackNotes };

  const system = [
    "You are a recruiter assistant evaluating a candidate's practical experience for job readiness.",
    'Return strictly JSON with keys: score (0..1 number) and notes (array of short strings).',
    'Consider provided level enums (NONE, FIRST, EXTENSIVE) and free-text descriptions.',
    'If multiple entries exist, consider the overall readiness (best/most relevant).',
    'Providing no experience should result in a score of 0. But providing some experience should always score higher than 0.',
  ].join(' ');

  const userJobDescription = [
    'Evaluate how well the practical experience matches the job description.',
    `Experiences: ${JSON.stringify(experiences)}`,
    `Job description: ${plainText(job.description)}`,
    'Return only JSON like {"score": 0.0, "notes": ["..."]}.',
    respondInLanguage(candidate),
  ].join('\n');

  const userPracticalExperience = [
    'Evaluate how well the practical experience matches the practical requirements of the job.',
    `Experiences: ${JSON.stringify(experiences)}`,
    `Job practical requirements: ${plainText(job.practicalExperience)}`,
    'Return only JSON like {"score": 0.0, "notes": ["..."]}.',
    respondInLanguage(candidate),
  ].join('\n');

  try {
    const [jobDescription, practicalExperience] = await Promise.all([
      runScoringPrompt(system, userJobDescription),
      runScoringPrompt(system, userPracticalExperience),
    ]);

    // Merge scores and notes from both LLM responses
    // Value practical experience 2/3 and job description at 1/3
    return {
      score: (jobDescription.score + 2 * practicalExperience.score) / 3,
      notes: jobDescription.notes.concat(practicalExperience.notes),
    };
  } catch (e) {
    logger.warn({ error: e }, 'Experience scoring fell back to rules');
    return { score: clamp01(fallbackBest), notes: fallbackNotes };
  }
}

// Position matching between desired employment types (candidate) and offered type (job)
function scorePosition(candidate: MWUserData, job: JobPosition): CriterionScore {
  const cand = (candidate.desiredEmploymentTypes || []).map(v => v!).filter(Boolean);
  const off = job.employmentType ? [job.employmentType!] : [];
  // If both are empty -> full match
  if (cand.length === 0 && off.length === 0) return { score: 1, notes: [] };
  // If either side is empty -> no match
  if (cand.length === 0 || off.length === 0) return { score: 0, notes: [] };
  // Requirement: if candidate selected all job employment types (here: includes offered type), score is 1
  if (off[0] && cand.includes(off[0]!)) return { score: 1, notes: [] };
  // Otherwise, give a partial score (candidate chose other types but missed the offered one)
  return { score: 0.5, notes: [] };
}

// ------------------ Public API ------------------

export async function calculateMatchingScore(candidate: MWUserData, job: JobPosition): Promise<MatchingScoreResult> {
  setLocale(candidate.chosenLanguage as 'de' | 'en');

  const notes: string[] = [];

  // The LLM-backed criteria run their prompts concurrently; the rest is synchronous.
  const [skills, exp] = await Promise.all([scoreSkills(candidate, job), scoreExperience(candidate, job)]);
  const languages = scoreLanguages(candidate, job);
  const soft = scoreSoftSkills(
    candidate.softSkills?.map(s => String(s)),
    job.softSkills?.map(s => String(s)),
  );
  const pos = scorePosition(candidate, job);

  notes.push(...skills.notes, ...languages.notes, ...exp.notes);

  const raw = {
    skills: clamp01(skills.score),
    languages: clamp01(languages.score),
    softSkills: clamp01(soft.score),
    experience: clamp01(exp.score),
    position: clamp01(pos.score),
  } as const;

  const weighted = {
    skills: Math.round(raw.skills * MATCH_WEIGHTS.skills * 100),
    languages: Math.round(raw.languages * MATCH_WEIGHTS.languages * 100),
    softSkills: Math.round(raw.softSkills * MATCH_WEIGHTS.softSkills * 100),
    experience: Math.round(raw.experience * MATCH_WEIGHTS.experience * 100),
    position: Math.round(raw.position * MATCH_WEIGHTS.position * 100),
  } as const;

  const total = weighted.skills + weighted.languages + weighted.softSkills + weighted.experience + weighted.position;

  return {
    total,
    weighted,
    raw,
    notes,
  };
}

export type CriterionKey = keyof MatchingScoreResult['raw'];

export function sortByCriterion<T extends MatchingScoreResult>(
  list: T[],
  key: CriterionKey,
  direction: 'asc' | 'desc' = 'desc',
): T[] {
  const factor = direction === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => (a.weighted[key] - b.weighted[key]) * factor);
}
